import { Home, Users, Building2, UsersRound, CheckSquare, Briefcase, Handshake, Shield, BarChart, AlertCircle, ClipboardList, Layers, Landmark, LayoutDashboard, DollarSign, Scale, Calendar, BarChart2, ChevronsLeft, ChevronsRight, LogOut, UserRound } from "lucide-react";
import { Link, useLocation } from "wouter";
import { OrganizationSwitcher, useClerk, useUser } from "@clerk/react";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarRail, SidebarFooter, useSidebar } from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useUserRole } from "@/hooks/use-user-role";
import { clerkPublishableKey } from "@/lib/auth";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminAccess } from "@/hooks/use-admin-access";
import { crmxV2KitEnabled, crmxSettingsEnabled } from "@/lib/feature-flags";
import { Settings as SettingsIcon } from "lucide-react";

// Set to true to show Project Manager and Control Centre in sidebar
const SHOW_PROJECT_MANAGER = false;

function ClerkAccountMenu() {
  const { user } = useUser();
  const { openUserProfile } = useClerk();
  const { signOut } = useAuth();
  const displayName = user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? "Account";
  const initials = displayName.slice(0, 2).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={`Open account menu for ${displayName}`}>
          <Avatar className="h-7 w-7">
            <AvatarImage src={user?.imageUrl} alt="" />
            <AvatarFallback className="text-xs">{initials}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="truncate">{displayName}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => openUserProfile()}>
          <UserRound />
          Manage account
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void signOut()}>
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Navigation structure following design guidelines
const crmNavigation = [
  {
    title: "Dashboard",
    url: "/",
    icon: Home,
  },
  {
    title: "Client Groups",
    url: "/client-groups",
    icon: UsersRound,
  },
  {
    title: "Contacts",
    url: "/contacts",
    icon: Users,
  },
  {
    title: "Accounts",
    url: "/accounts",
    icon: Building2,
  },
  {
    title: "Deals",
    url: "/deals",
    icon: Briefcase,
  },
  {
    title: "Pipeline",
    url: "/deals/pipeline",
    icon: LayoutDashboard,
  },
  {
    title: "Deal Tasks",
    url: "/crm/deal-tasks",
    icon: CheckSquare,
  },
  {
    title: "Lenders",
    url: "/lenders",
    icon: Landmark,
  },
  {
    title: "Partners",
    url: "/partners",
    icon: Handshake,
  },
];

const projectManagerNavigation = [
  {
    title: "Dashboard",
    url: "/project-manager/dashboard",
    icon: Home,
  },
  {
    title: "Projects",
    url: "/project-manager/projects",
    icon: Briefcase,
  },
  {
    title: "All Tasks",
    url: "/project-manager/tasks",
    icon: CheckSquare,
  },
  {
    title: "Sprints",
    url: "/project-manager/sprints",
    icon: Layers,
  },
  {
    title: "Test Checklists",
    url: "/project-manager/testing-checklists",
    icon: ClipboardList,
  },
  {
    title: "Issues",
    url: "/issues",
    icon: AlertCircle,
  },
];

const controlCentreNavigation = [
  {
    title: "Performance",
    url: "/project-manager/performance",
    icon: BarChart,
  },
];

const commissionNavigation = [
  {
    title: "Commission",
    url: "/commission/transactions",
    icon: DollarSign,
  },
];

const complianceNavigation = [
  {
    title: "Complaints",
    url: "/compliance/complaints",
    icon: Scale,
    testId: "link-complaints",
  },
  {
    title: "Calendar",
    url: "/compliance/calendar",
    icon: Calendar,
    testId: "link-calendar",
  },
  {
    title: "Compliance Dashboard",
    url: "/compliance/dashboard",
    icon: BarChart2,
    testId: "link-compliance-dashboard",
  },
];

const ROLE_LABELS: Record<string, string> = {
  system_admin: "System Admin",
  org_owner: "Org Owner",
  compliance_officer: "Compliance Officer",
  admin: "Admin",
  manager: "Manager",
  broker: "Broker",
  support: "Support",
  readonly: "Read Only",
};

export function AppSidebar() {
  const isAdmin = useAdminAccess();
  const [location] = useLocation();
  const { role, canSeeCompliance, canSeeCommission } = useUserRole();
  const { toggleSidebar, state } = useSidebar();

  return (
    <Sidebar collapsible="icon">
      <SidebarContent>
        {/* CRM Module */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            CRM Module
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {crmNavigation.map((item) => {
                const isActive = location === item.url;
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive} data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                      <Link href={item.url}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Control Centre Module — role-aware visibility */}
        {(canSeeCommission || canSeeCompliance) && (
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Control Centre
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {canSeeCommission && commissionNavigation.map((item) => {
                const isActive =
                  location === item.url ||
                  location.startsWith("/commission");
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive} data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                      <Link href={item.url}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
              {/* Compliance section within Control Centre */}
              {canSeeCompliance && complianceNavigation.map((item) => {
                const isActive =
                  location === item.url ||
                  (item.url === "/compliance/complaints" && location.startsWith("/compliance/complaints")) ||
                  location === item.url;
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive} data-testid={item.testId}>
                      <Link href={item.url}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        )}

        {/* Project Manager Module - Hidden (set SHOW_PROJECT_MANAGER to true to reactivate) */}
        {SHOW_PROJECT_MANAGER && (
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Project Manager
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {projectManagerNavigation.map((item) => {
                const isActive = location === item.url;
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive} data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                      <Link href={item.url}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        )}

        {/* Control Centre Module - Hidden (set SHOW_PROJECT_MANAGER to true to reactivate) */}
        {SHOW_PROJECT_MANAGER && (
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Control Centre
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {controlCentreNavigation.map((item) => {
                const isActive = location === item.url;
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive} data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                      <Link href={item.url}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        )}

        {/* Admin Portal */}
        {isAdmin && (
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location === "/project-manager/settings"} data-testid="link-admin-portal">
                  <Link href="/project-manager/settings">
                    <Shield className="h-4 w-4" />
                    <span>Admin Portal</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarFooter>
        {crmxV2KitEnabled && crmxSettingsEnabled && isAdmin && (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={location.startsWith("/settings")} data-testid="link-settings">
                <Link href="/settings/data-hygiene">
                  <SettingsIcon />
                  <span>Settings</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
        <button
          onClick={toggleSidebar}
          data-testid="button-sidebar-collapse"
          aria-label={state === "collapsed" ? "Expand sidebar" : "Collapse sidebar"}
          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors w-full"
        >
          {state === "collapsed" ? (
            <ChevronsRight className="h-4 w-4 shrink-0" />
          ) : (
            <>
              <ChevronsLeft className="h-4 w-4 shrink-0" />
              <span>Collapse</span>
            </>
          )}
        </button>
        <div className="flex flex-col gap-2 p-2">
          {clerkPublishableKey ? (
            <div className="flex items-center gap-2">
              <ClerkAccountMenu />
              <OrganizationSwitcher
                hidePersonal
                appearance={{
                  elements: {
                    organizationSwitcherTrigger: "text-sm px-2 py-1 rounded hover:bg-muted",
                  },
                }}
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>CI Mode</span>
            </div>
          )}
          <Badge variant="secondary" data-testid="badge-user-role" className="w-fit text-xs">
            {ROLE_LABELS[role] || role}
          </Badge>
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
